import React from 'react';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error) => void;
}

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }: { error?: Error }) {
  const { t } = useTranslation();
  
  const handleRetry = () => {
    try {
      sessionStorage.removeItem('dc:chunk-reload-at');
    } catch {
      // ignore
    }
    window.location.reload();
  };

  const msg = error?.message || '';
  const isChunkError =
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t('common.error', 'Something went wrong')}
        </h2>
        
        <p className="text-gray-600 mb-4">
          {isChunkError
            ? t('common.refreshForUpdate', 'A new version was deployed. Please refresh the page.')
            : error?.message?.includes('Authentication') 
            ? t('auth.required', 'Authentication required — try signing in again')
            : t('common.errorDescription', 'An unexpected error occurred. Please try again.')
          }
        </p>
        
        <button
          onClick={handleRetry}
          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    </div>
  );
}

export function ErrorBoundary({ children, onError }: ErrorBoundaryProps) {
  return (
    <ErrorBoundaryClass onError={onError}>
      {children}
    </ErrorBoundaryClass>
  );
}
